import React, { createContext, useEffect, useLayoutEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { RootState } from '@store/index'
import {
  fetchAllCampaigns,
  fetchAllContacts,
  fetchAllConversations,
  fetchAllClinics,
  fetchAllCallLogs,
  fetchAllContactGroups,
  restoreSession,
  fetchAllFavoritedUsers,
  fetchAllUsers,
  fetchAllInternalChats,
  fetchAllFaxTags,
  fetchAllFax,
  fetchAllFaxFolders,
  fetchReportsTemplate,
} from '@store/actions'
import {
  clearSelectedClinic,
  setSelectedClinic,
  setStaffAssignedClinicIds,
} from '@store/reducers/clinic.reducer'
import { isStaff, readPid } from '@utils/accountSession.util'
import { loadRow } from '@services/api/provider.api'
import { clinicIdsFromProvider } from '@utils/staffClinicScope.util'
import { AudioContextType } from '@dataTypes/audio.types'
import { LeftSideBar, Navigation } from '@components/layout/index'
import { AppRoutes } from './routes'
import SidePanel from '@components/layout/SidePanel/SidePanel.component'
import PatientTray from '@components/layout/PatientTray/PatientTray.component'
import AIAssistSidePanel from '@components/layout/AIAssistSidePanel/AIAssistSidePanel.component'
import InternalChatSidePanel from '@components/layout/InternalChatSidePanel/InternalChatSidePanel.component'
import PhoneSidePanel from '@components/layout/PhoneSidePanel/PhoneSidePanel.component'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  toggleSidePanel,
  closeSidePanel,
  setActiveSidePanel,
} from '@store/reducers/uiSlice.reducer'
import {
  stopRecording,
  startRecordingm,
  setuponDataAvailablem,
} from '@utils/mediarecored.util'
import { connectSoundAPI } from '@utils/connect.util'
import { IMediaRecorder } from 'extendable-media-recorder'
import './App.css'
import './assets/css/dashboard-bs5v1.css'
import './fonts/MingCute/MingCute.css'
import { createNewTwilioDevice } from '@utils/device.twilio'
import { ActiveCallProvider } from './contexts/ActiveCallContext'
import {
  connectFormsSocket,
  connectNotificationsSocket,
  reconnectCallsSocket,
  reconnectFaxSocket,
  disconnectFaxSocket,
  reconnectMessagingSockets,
  disconnectMessagingSockets,
  getFormsSocket,
  getNotificationsSocket,
  disconnectNotificationsSocket,
  disconnectAllStaffRealtimeSockets,
} from '@services/websocket/socket'
import {
  createNotificationToast,
  createPatientSignedFormsToast,
  getChartingNotificationToastCopy,
  getClaimsNotificationToastCopy,
  getBillingNotificationToastCopy,
  getMessageCenterNotificationToastCopy,
} from '@utils/notificationToast.util'
import {
  addRealtimeNotification,
  applyRealtimeDismissed,
  applyRealtimeRead,
  applyRealtimeReadAll,
  applyRealtimeUnread,
  clearNotifications,
} from '@store/reducers/notification.reducer'
import { fetchNotifications } from '@store/actions/notification.action'
import { mapBackendNotification } from '@services/api/notification.api'
import type {
  BackendNotification,
  Notification as NotificationType,
} from '@dataTypes/notification.types'
import { NotificationTriggerType } from '@dataTypes/notification.types'
import ProviderSidePanel from '@components/pages/Provider/Sidepanel/ProviderSidePanel.component'
import {
  NotificationToastProvider,
  useNotificationToast,
} from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import NotificationToastContainer from '@components/common/NotificationToast/NotificationToastContainer.component'
import FileSelectionOverlay from '@components/layout/FileSelectionOverlay/FileSelectionOverlay.component'
import { IClinic } from '@dataTypes/clinic.type'
import InternalChatSocketListener from '@components/layout/InternalChatSidePanel/InternalChatSocketListener.component'
import ChatSocketListener from '@components/layout/ChatSocketListener/ChatSocketListener.component'
import FaxSocketListener from '@components/layout/FaxSocketListener/FaxSocketListener.component'
import CallAiSocketListener from '@components/layout/CallAiSocketListener/CallAiSocketListener.component'
import EmailSocketListener from '@components/layout/EmailSocketListener/EmailSocketListener.component'
import PortRequestSocketListener from '@components/layout/PortRequestSocketListener/PortRequestSocketListener.component'
import { NoteEditorOverlayProvider } from '@components/common/NoteEditorOverlay'
import { ErrorBoundary } from '@components/common/ErrorBoundary'
import {
  isKioskDeviceMode,
  isStandalonePatientFormsPath,
} from '@utils/standalonePatientForms.util'

export const AudioContext = createContext<AudioContextType | null>(null)

const NotificationToastWrapper: React.FC = () => {
  const { toasts, removeToast, showToast } = useNotificationToast()
  const { showLoading } = useGlobalLoadingActions()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(
    (state: RootState) => state.auth.isAuthenticated,
  )
  const dbName = useAppSelector((state: RootState) => state.auth.dbName)
  const selectedClinic = useAppSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const formCompletionCountsRef = useRef<Map<string, number>>(new Map())
  const formCompletionTimersRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map())

  React.useEffect(() => {
    ;(window as any).__showNotificationToast = showToast
  }, [showToast])

  // Forms realtime: show toast when a patient completes forms (WebSocket)
  useEffect(() => {
    if (!isAuthenticated || !selectedClinic?.clinic_id || !dbName) return
    connectFormsSocket()
    const socket = getFormsSocket()
    if (!socket) return
    const formCompletionTimers = formCompletionTimersRef.current
    const formCompletionCounts = formCompletionCountsRef.current

    const handleAssignmentCompleted = (payload: {
      patientName?: string
      patientId?: string | null
      assignmentId?: string
    }): void => {
      const patientName = payload.patientName ?? 'A patient'
      const patientId = payload.patientId ?? undefined
      const patientKey = patientId ?? patientName
      const currentCount = formCompletionCounts.get(patientKey) ?? 0
      formCompletionCounts.set(patientKey, currentCount + 1)

      const existingTimer = formCompletionTimers.get(patientKey)
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      const nextTimer = setTimeout(() => {
        const formCount = formCompletionCounts.get(patientKey) ?? 1
        const toast = createPatientSignedFormsToast(
          patientName,
          formCount,
          () => {
            showLoading({ container: 'body' })
            navigate('/form-builder', {
              state: {
                tab: 'completed',
                patientId,
                patientName,
              },
            })
          },
        )
        showToast(toast)
        formCompletionCounts.delete(patientKey)
        formCompletionTimers.delete(patientKey)
      }, 1500)

      formCompletionTimers.set(patientKey, nextTimer)
    }

    socket.on('forms.assignment.completed', handleAssignmentCompleted)
    return () => {
      socket.off('forms.assignment.completed', handleAssignmentCompleted)
      formCompletionTimers.forEach((timer) => clearTimeout(timer))
      formCompletionTimers.clear()
      formCompletionCounts.clear()
    }
  }, [
    navigate,
    showToast,
    showLoading,
    isAuthenticated,
    selectedClinic?.clinic_id,
    dbName,
  ])

  // Notifications realtime: surface live toast + sync redux store across tabs/staff
  useEffect(() => {
    if (!isAuthenticated || !selectedClinic?.clinic_id || !dbName) {
      disconnectNotificationsSocket()
      dispatch(clearNotifications())
      return
    }
    connectNotificationsSocket()
    const socket = getNotificationsSocket()
    if (!socket) return

    const handleCreated = (payload: {
      notification: BackendNotification
      recipient_user_ids?: string[]
    }): void => {
      if (!payload?.notification) return
      const mapped: NotificationType = mapBackendNotification(
        payload.notification,
      )
      dispatch(addRealtimeNotification(mapped))

      const isFrontdeskOriginatedPortEvent =
        mapped.type === NotificationTriggerType.PORT_REQUEST_SUBMITTED ||
        mapped.type === NotificationTriggerType.PORT_REQUEST_RESUBMITTED
      if (isFrontdeskOriginatedPortEvent) {
        return
      }

      const chartingCopy = getChartingNotificationToastCopy(mapped.type)
      const claimsCopy = getClaimsNotificationToastCopy(mapped.type)
      const billingCopy = getBillingNotificationToastCopy(mapped.type)
      const messageCenterCopy = getMessageCenterNotificationToastCopy(
        mapped.type,
      )
      const specCopy =
        chartingCopy ?? claimsCopy ?? billingCopy ?? messageCenterCopy
      const moduleCopy = claimsCopy ?? billingCopy ?? messageCenterCopy
      const actionButtonText =
        specCopy?.actionButtonText ?? mapped.actionButtonText ?? 'View'
      const shouldNavigate = messageCenterCopy?.openInternalChat
        ? true
        : moduleCopy
          ? moduleCopy.fallbackPath != null
          : true
      const toast = {
        ...createNotificationToast(
          {
            id: mapped.id,
            notification_id: mapped.notification_id,
            title: specCopy?.title ?? mapped.title,
            message: specCopy?.message ?? mapped.message,
            actionButtonText,
            priority: mapped.priority,
          },
          shouldNavigate
            ? () => {
                if (messageCenterCopy?.openInternalChat) {
                  dispatch(setActiveSidePanel('chat'))
                  return
                }
                const navState =
                  mapped.action_metadata ??
                  messageCenterCopy?.navState ??
                  (claimsCopy?.openClaim && mapped.source_record_id
                    ? {
                        viewClaim: true,
                        claimId: mapped.source_record_id,
                        ...(mapped.patient_id
                          ? { patientId: mapped.patient_id }
                          : {}),
                      }
                    : null)
                const fallbackPath =
                  moduleCopy?.fallbackPath ??
                  (mapped.type ===
                  NotificationTriggerType.CHART_NEW_CONDITION_ADDED
                    ? '/charting'
                    : mapped.type === NotificationTriggerType.CHART_ENTRY_EDITED
                      ? '/charting/history'
                      : '/notifications')
                navigate(
                  mapped.action_url || fallbackPath || '/notifications',
                  navState ? { state: navState } : undefined,
                )
              }
            : undefined,
        ),
        ...(moduleCopy?.secondaryButtonText
          ? { secondaryButtonText: moduleCopy.secondaryButtonText }
          : {}),
      }
      showToast(toast)
    }

    const handleRead = (payload: { notification_id: string }): void => {
      if (!payload?.notification_id) return
      dispatch(applyRealtimeRead({ id: payload.notification_id }))
    }
    const handleUnread = (payload: { notification_id: string }): void => {
      if (!payload?.notification_id) return
      dispatch(applyRealtimeUnread({ id: payload.notification_id }))
    }
    const handleDismissed = (payload: { notification_id: string }): void => {
      if (!payload?.notification_id) return
      dispatch(applyRealtimeDismissed({ id: payload.notification_id }))
    }
    const handleReadAll = (): void => {
      dispatch(applyRealtimeReadAll())
    }

    socket.on('notification.created', handleCreated)
    socket.on('notification.read', handleRead)
    socket.on('notification.unread', handleUnread)
    socket.on('notification.dismissed', handleDismissed)
    socket.on('notification.read_all', handleReadAll)

    return () => {
      socket.off('notification.created', handleCreated)
      socket.off('notification.read', handleRead)
      socket.off('notification.unread', handleUnread)
      socket.off('notification.dismissed', handleDismissed)
      socket.off('notification.read_all', handleReadAll)
    }
  }, [
    isAuthenticated,
    selectedClinic?.clinic_id,
    dbName,
    dispatch,
    showToast,
    navigate,
  ])

  const handleToastAction = (id: string): void => {
    // Toast action clicked
  }

  return (
    <NotificationToastContainer
      toasts={toasts}
      onClose={removeToast}
      onAction={handleToastAction}
    />
  )
}

function App(): React.ReactElement {
  const viewProvider = useAppSelector((state) => state.provider.viewProvider)
  const activeSidePanel = useAppSelector((state) => state.ui.activeSidePanel)
  // react state object for toothnumber
  const [toothnumber, setToothNumber] = React.useState(1)
  // react state for tooth position
  const [position, setPosition] = React.useState('facial')
  // tracker for current tooth position and type
  const currentToothRef = React.useRef({
    number: 1,
    position: 'facial',
    type: 'pocket_depth',
    pause: false,
  })
  // react ref object for toothnumber
  const toothref = React.useRef<number>(1)
  // react ref object for tooth position
  const positionref = React.useRef<string>('facial')
  // react ref object pause in recording
  const paused = React.useRef<boolean>(false)
  // react ref object to check if recording
  const isRecording = React.useRef<boolean>(false)
  // react ref object to store media stream object
  const stream = React.useRef<MediaStream | null>(null)
  // react ref object to store media recorder object
  const recorder = React.useRef<IMediaRecorder | null>(null)
  // react ref object to interval for functions in setinterval
  const interval = React.useRef<NodeJS.Timer | null>(null)
  // react ref object to store audio blob
  const audiofiles = React.useRef<Blob[]>([])
  // react ref object to store audio streams
  const intervalms = React.useRef<number>(5000)
  // react ref object to store volume / sound amplitude
  const streamfilter = React.useRef<number>(8000)
  // react ref object for previous transcript
  const prev_context = React.useRef<string>('no context')
  // react ref object for list of previous state of upper tooth before change
  const prevTeethSet1Ref = React.useRef<any[]>([])
  // react ref object for list of previous state of bottom tooth before change
  const prevTeethSet2Ref = React.useRef<any[]>([])
  // react ref object to indicate change in tooth number
  const toothChange = React.useRef<boolean>(false)
  // react state to check if undo command is invoked
  const [undo, setUndo] = React.useState(false)
  // react state where changes are stored
  const [toothfeature, setToothFeature] = React.useState({
    update: ['no update'],
    pd_mesial: 0,
    pd_buccal: 0,
    pd_distal: 0,
    gm_mesial: 0,
    gm_buccal: 0,
    gm_distal: 0,
    bl_mesial: 0,
    bl_buccal: 0,
    bl_distal: 0,
    ca_mesial: 0,
    ca_buccal: 0,
    ca_distal: 0,
    pl_mesial: 0,
    pl_buccal: 0,
    pl_distal: 0,
    su_mesial: 0,
    su_buccal: 0,
    su_distal: 0,
    furcation: 0,
    mobility: 0,
  })
  // react state to check if clear command is invoked
  const [clear, setClear] = React.useState(false)
  // react state used to combine dataset from upper and lower tooth
  const [totalDataset, setTotalDataset] = React.useState([] as any[])
  const { isAuthenticated, dbName } = useAppSelector(
    (state: RootState) => state.auth,
  )
  const selectedClinic = useAppSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isStandaloneLayout = isStandalonePatientFormsPath(location.pathname)
  const isSidePanelOpen = useAppSelector((state) => state.ui.isSidePanelOpen)
  const kioskDeviceMode = isKioskDeviceMode()

  // Connect forms + messaging WebSockets when staff is authenticated and has clinic/db selected
  useEffect(() => {
    if (kioskDeviceMode || isStandaloneLayout) {
      disconnectAllStaffRealtimeSockets()
      return
    }
    if (isAuthenticated && selectedClinic && dbName) {
      connectFormsSocket()
      reconnectCallsSocket()
      reconnectMessagingSockets()
      connectNotificationsSocket()
      reconnectFaxSocket()
    } else {
      disconnectMessagingSockets()
      disconnectNotificationsSocket()
      disconnectFaxSocket()
    }
  }, [
    isAuthenticated,
    selectedClinic,
    dbName,
    isStandaloneLayout,
    kioskDeviceMode,
  ])

  /** Patient kiosk/public forms: no CRM nav, sidebars, or panels. */
  const showProviderSidePanelRoutes = ['/provider-management']
  const isProviderPage = showProviderSidePanelRoutes.some(
    (route) => location.pathname === route,
  )
  const isPatientProfilePage = location.pathname.startsWith('/patient-profile/')
  const sidePanelOpenBeforeProfileRef = useRef<boolean | null>(null)

  // Disconnect staff realtime as early as possible on patient-only routes.
  useLayoutEffect(() => {
    if (kioskDeviceMode || isStandaloneLayout) {
      disconnectAllStaffRealtimeSockets()
    }
  }, [isStandaloneLayout, kioskDeviceMode])

  // Hide patient side panel on full profile; restore when leaving if it was open before
  useEffect(() => {
    if (isPatientProfilePage) {
      if (sidePanelOpenBeforeProfileRef.current === null) {
        sidePanelOpenBeforeProfileRef.current =
          isSidePanelOpen && activeSidePanel === 'main'
      }
      dispatch(closeSidePanel())
      return
    }

    if (sidePanelOpenBeforeProfileRef.current) {
      dispatch(setActiveSidePanel('main'))
    }
    sidePanelOpenBeforeProfileRef.current = null
  }, [isPatientProfilePage, isSidePanelOpen, activeSidePanel, dispatch])

  // Close patient side panel when navigating to provider page so it is not shown there
  useEffect(() => {
    if (isProviderPage) {
      dispatch(closeSidePanel())
    }
  }, [isProviderPage, dispatch])

  // Close provider side panel when navigating away from provider page (selection state is retained for when user returns)
  useEffect(() => {
    if (!isProviderPage) {
      dispatch(closeSidePanel())
    }
  }, [isProviderPage, dispatch])

  // Restore session on boot via HttpOnly refresh cookie (survives page refresh)
  useEffect(() => {
    if (isKioskDeviceMode()) return
    if (isStandalonePatientFormsPath(location.pathname)) return
    dispatch(restoreSession())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount

  useEffect(() => {
    if (kioskDeviceMode || isStandaloneLayout) return
    if (isAuthenticated) {
      // Reconnect calls socket with current token so server can put us in db room and we receive call-status
      dispatch(fetchAllClinics())
        .then(async (res) => {
          const clinics = Array.isArray(res.payload) ? res.payload : []

          // Staff can only access their assigned clinics
          let allowedClinics: IClinic[] = clinics
          if (isStaff()) {
            const pid = readPid()
            const provider = pid ? await loadRow(pid) : null
            const assignedIds = provider ? clinicIdsFromProvider(provider) : []
            dispatch(setStaffAssignedClinicIds(assignedIds))
            const allowed = new Set(assignedIds)
            allowedClinics =
              assignedIds.length > 0
                ? clinics.filter((c) => allowed.has(c.clinic_id))
                : []
          } else {
            dispatch(setStaffAssignedClinicIds(null))
          }

          let savedClinic: IClinic | null =
            allowedClinics.length > 0 ? allowedClinics[0] : null
          const savedClinicId = localStorage.getItem('clinic_id')
          if (savedClinicId && allowedClinics.length > 0) {
            savedClinic =
              allowedClinics.find((c) => c.clinic_id === savedClinicId) ??
              allowedClinics[0]
          }

          if (savedClinic) {
            dispatch(setSelectedClinic(savedClinic))
            createNewTwilioDevice(dispatch)
          } else {
            dispatch(clearSelectedClinic())
          }
          dispatch(fetchAllConversations())
          dispatch(fetchAllInternalChats())
          dispatch(fetchAllFavoritedUsers())
          dispatch(fetchAllContactGroups())
          dispatch(fetchAllContacts())
          dispatch(fetchAllFax())
          dispatch(fetchAllFaxTags())
          dispatch(fetchAllFaxFolders())
          dispatch(fetchAllCampaigns())
          dispatch(fetchAllCallLogs())
          dispatch(fetchAllUsers())
          dispatch(fetchNotifications())
          dispatch(fetchReportsTemplate())
        })
        .catch((error) => {
          console.warn('Failed to fetch clinics:', error)
          dispatch(clearSelectedClinic())
        })
    } else {
      dispatch(clearSelectedClinic())
      dispatch(setStaffAssignedClinicIds(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isStandaloneLayout, kioskDeviceMode])

  if (kioskDeviceMode && location.pathname !== '/forms/kiosk') {
    return <Navigate to="/forms/kiosk" replace />
  }

  return (
    <>
      {/* provides global context related to voice assitant */}
      <AudioContext.Provider
        value={{
          stopRecording: stopRecording,
          startRecording: startRecordingm,
          setuponDataAvailable: setuponDataAvailablem,
          paused: paused,
          isRecording: isRecording,
          stream: stream,
          recorder: recorder,
          interval: interval,
          audiofiles: audiofiles,
          intervalms: intervalms,
          streamfilter: streamfilter,
          setToothNumber: setToothNumber,
          toothnumber: toothnumber,
          setPosition: setPosition,
          position: position,
          toothfeature: toothfeature,
          setToothFeature: setToothFeature,
          toothref: toothref,
          positionref: positionref,
          totalDataset: totalDataset,
          setTotalDataset: setTotalDataset,
          clear: clear,
          setClear: setClear,
          prev_context: prev_context,
          connect: connectSoundAPI,
          currentTooth: currentToothRef,
          prevTeethSet1Ref: prevTeethSet1Ref,
          prevTeethSet2Ref: prevTeethSet2Ref,
          undo: undo,
          setUndo,
          toothChange,
        }}
      >
        <NotificationToastProvider>
          <NoteEditorOverlayProvider>
            {isAuthenticated ? (
              isStandaloneLayout ? (
                <ErrorBoundary variant="page" resetKeys={[location.pathname]}>
                  <AppRoutes />
                </ErrorBoundary>
              ) : (
                <ActiveCallProvider>
                  <>
                    <div className="app-container">
                      <Navigation />
                      <div className="body-container">
                        <LeftSideBar />
                        <div
                          className={`main-content ${location.pathname === '/message' ? 'mc-message' : ''}`}
                        >
                          <div className="page-content">
                            <ErrorBoundary
                              variant="page"
                              resetKeys={[location.pathname]}
                            >
                              <AppRoutes />
                            </ErrorBoundary>
                          </div>

                          {!isProviderPage && (
                            <SidePanel
                              isHidden={
                                !(isSidePanelOpen && activeSidePanel === 'main')
                              }
                              toggle={() => dispatch(toggleSidePanel())}
                            />
                          )}
                          <AIAssistSidePanel
                            isHidden={
                              !(isSidePanelOpen && activeSidePanel === 'ai')
                            }
                            toggle={() => dispatch(toggleSidePanel())}
                          />
                          <InternalChatSidePanel
                            isHidden={
                              !(isSidePanelOpen && activeSidePanel === 'chat')
                            }
                            toggle={() => dispatch(toggleSidePanel())}
                          />
                          <PhoneSidePanel
                            isHidden={
                              !(isSidePanelOpen && activeSidePanel === 'phone')
                            }
                            toggle={() => dispatch(toggleSidePanel())}
                          />
                          {isProviderPage && (
                            <ProviderSidePanel
                              provider={viewProvider}
                              isHidden={
                                !(isSidePanelOpen && activeSidePanel === 'main')
                              }
                              toggle={() => dispatch(toggleSidePanel())}
                            />
                          )}
                          <PatientTray />
                        </div>
                      </div>
                    </div>
                    <NotificationToastWrapper />
                    <FileSelectionOverlay />
                    <InternalChatSocketListener />
                    <ChatSocketListener />
                    <FaxSocketListener />
                    <CallAiSocketListener />
                    <EmailSocketListener />
                    <PortRequestSocketListener />
                  </>
                </ActiveCallProvider>
              )
            ) : (
              <ErrorBoundary variant="page" resetKeys={[location.pathname]}>
                <AppRoutes />
              </ErrorBoundary>
            )}
          </NoteEditorOverlayProvider>
        </NotificationToastProvider>
      </AudioContext.Provider>
    </>
  )
}

export default App
